import { Box, Button, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import Restaurant from '../../data/models/restaurant';
import { Delete, Edit, KeyboardArrowRight } from '@mui/icons-material';
import { isRestaurantManager } from '../../utils/userRoles';
import { Roles } from '../../data/auth/roles';
import { useTranslation } from 'react-i18next';

interface Props {
  restaurant: Restaurant;
  isEditable?: boolean;
  roles?: Roles;
  onClick?: (restaurant: Restaurant) => void;
  loading?: boolean;
  onEdit?: () => void;
  onDelete?: () => void;
}

const RestaurantListItem = ({
  restaurant,
  isEditable,
  roles,
  onClick,
  loading,
  onEdit,
  onDelete,
}: Props) => {
  const { t } = useTranslation();

  return (
    <Box
      key={restaurant.id}
      onClick={() => onClick && onClick(restaurant)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        py: 2.5,
        px: 3,
        mb: 1.5,
        borderRadius: 2,
        backgroundColor: 'background.paper',
        transition: 'all 0.2s ease-in-out',
        position: 'relative',
        '&:hover': {
          bgcolor: 'grey.50',
          cursor: 'pointer',
          '& .arrow-icon': {
            transform: 'translateX(4px)',
            opacity: 1,
          },
        },
        '&:active': {
          bgcolor: 'grey.100',
        },
        border: '1px solid',
        borderColor: 'grey.100',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
      }}
    >
      <Box
        component="img"
        src={restaurant.logoUrl}
        alt={restaurant.name}
        sx={{
          width: 64,
          height: 64,
          borderRadius: 2,
          objectFit: 'contain',
          mr: 3,
          border: '1px solid',
          borderColor: 'grey.100',
          p: 1,
          bgcolor: 'white',
          boxShadow: '0 2px 4px rgba(0,0,0,0.04)',
        }}
      />
      <Box
        sx={{
          flex: 1,
          minWidth: 0,
        }}
      >
        <Typography
          variant="h6"
          sx={{
            fontWeight: 500,
            mb: 0.5,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            color: 'text.primary',
            fontSize: '1.1rem',
          }}
        >
          {restaurant.name}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 0.5,
            color: 'text.secondary',
            fontWeight: 500,
          }}
        >
          {restaurant.distance
            ? t('customer.home.distance.km-away', {
                distance: restaurant.distance.toFixed(1),
              })
            : t('customer.home.distance.not-available')}
        </Typography>
      </Box>
      {isEditable && (
        <Box display="flex" justifyContent="flex-end" sx={{ ml: 2 }}>
          {(isRestaurantManager(roles) || roles?.isSuperAdmin) && (
            <Button
              onClick={(e) => {
                e.stopPropagation();
                if (onEdit) onEdit();
              }}
              startIcon={<Edit />}
            />
          )}
          {roles?.isSuperAdmin && (
            <LoadingButton
              onClick={(e) => {
                e.stopPropagation();
                if (onDelete) onDelete();
              }}
              loading={loading}
              startIcon={<Delete />}
              color="error"
            />
          )}
        </Box>
      )}
      {!isEditable && (
        <Box
          className="arrow-icon"
          sx={{
            ml: 2,
            color: 'primary.main',
            transition: 'all 0.2s ease-in-out',
            opacity: 0.5,
            display: { xs: 'none', sm: 'flex' },
            alignItems: 'center',
          }}
        >
          <KeyboardArrowRight />
        </Box>
      )}
    </Box>
  );
};

export default RestaurantListItem;
